// Recovering from a stale chunk after a deploy — and never leaving a blank page behind.
//
// When Vite ships a new build every lazy chunk gets a new hash. A tab opened beforehand still
// holds the OLD index.html, so its next route-level import() 404s and `#root` stays empty. One
// hard reload fixes it by pulling the fresh index.html.
//
// THE GUARD IS RIGHT AND USED TO END IN SILENCE. A reload loop on a cause that is not a stale
// deploy (offline, adblocker, CSP) is worse than one blank page, so a second attempt inside 10s is
// suppressed — but suppression used to `return`, leaving a white screen with nothing to press.
// That happened repeatedly on 2026-09-09 during a run of deploys, and the fix was always a manual
// refresh that nothing on screen asked for. Now it tells the boot placeholder in index.html to say
// so; that placeholder is still on the page precisely because React never mounted.
//
// Its own module so it can be tested: importing main.jsx runs `createRoot` against a `#root` that
// does not exist under a test renderer.

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',   // Chrome
  'error loading dynamically imported module',     // Firefox
  'Importing a module script failed',              // Safari
];

export function isChunkLoadError(message) {
  if (typeof message !== 'string') return false;
  return CHUNK_ERROR_PATTERNS.some((p) => message.includes(p));
}

/** @returns 'not-a-chunk-error' | 'reloaded' | 'suppressed' — returned so it can be asserted. */
export function maybeReloadForStaleChunk(message, {
  now = Date.now,
  // A private window or a locked-down browser has no sessionStorage. Losing the loop guard is
  // acceptable there; throwing on the way past would lose the app.
  storage = (() => { try { return globalThis.sessionStorage ?? null; } catch { return null; } })(),
  reload = () => globalThis.location?.reload(),
  giveUp = (why) => globalThis.versoBootFailed?.(why),
} = {}) {
  if (!isChunkLoadError(message)) return 'not-a-chunk-error';
  const key = 'chunk-reload-at';
  let last = 0;
  try { last = Number(storage?.getItem(key) || 0); } catch { last = 0; }
  if (now() - last < 10_000) {
    giveUp('This page didn’t finish loading, and reloading once didn’t fix it. '
         + 'Try again — if it keeps happening, a new version may still be rolling out.');
    return 'suppressed';
  }
  try { storage?.setItem(key, String(now())); } catch { /* see above */ }
  reload();
  return 'reloaded';
}
